import React, { useRef } from 'react';
import styled from 'styled-components';

import useMeasuredWidth from 'components/Intelligence/useMeasuredWidth';

import { px } from '../theme/tokens';

const ROW_GAP = 12;

/** Narrowest a card may get before the row drops a column, per drawn column count. */
const MIN_CARD_WIDTH: Record<number, number> = { 2: 360, 3: 280, 4: 220, 5: 150, 6: 150 };

/**
 * Columns that fit `width` with every card at least `min` wide, never more than drawn. An
 * unmeasured row (0, as in jsdom) keeps the drawn count.
 */
export const fitColumns = (columns: number, width: number, min: number, gap: number): number => {
  if (width <= 0) {
    return columns;
  }
  for (let count = columns; count > 1; count -= 1) {
    if (count * min + (count - 1) * gap <= width) {
      return count;
    }
  }
  return 1;
};

/**
 * Rows only ever split evenly: the largest divisor of the card count that fits (4: 4, 2+2, 1;
 * 6: 6, 3+3, 2+2+2, 1), so no row strands a card or leaves an empty slot.
 */
export const balanceColumns = (fit: number, items: number): number => {
  if (items <= 0) {
    return fit;
  }
  for (let count = Math.min(fit, items); count > 1; count -= 1) {
    if (items % count === 0) {
      return count;
    }
  }
  return 1;
};

export interface IRowLayout {
  /** Grid tracks. */
  columns: number;
  /** Cards per visual row, top to bottom. */
  rows: number[];
}

/**
 * The even split from `balanceColumns`, except for a count with no even divisor that fits
 * (5, 7, ...): it then takes two rows, the larger first (5: 3 + 2, 7: 4 + 3), each stretched to the
 * full width, and drops to one per row only when even the larger row does not fit.
 */
export const rowLayout = (fit: number, items: number): IRowLayout => {
  const even = balanceColumns(fit, items);
  const first = Math.ceil(items / 2);
  if (even === 1 && items > 2 && fit >= first && fit < items) {
    const second = items - first;
    return { columns: first * second, rows: [first, second] };
  }
  return {
    columns: even,
    rows: Array.from({ length: Math.ceil(items / even) }, (_, index) =>
      Math.min(even, items - index * even)
    )
  };
};

/** Track span of each card: the grid's tracks divided by the cards in that card's row. */
export const cardSpans = ({ columns, rows }: IRowLayout): number[] =>
  rows.flatMap(cards => Array.from({ length: cards }, () => columns / cards));

const Grid = styled.div<{ $count: number; $gap: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $count }) => $count}, minmax(0, 1fr));
  gap: ${({ $gap }) => px($gap)};
  min-width: 0;
`;

const SpanCell = styled.div<{ $span: number }>`
  display: flex;
  grid-column: span ${({ $span }) => $span};
  min-width: 0;

  > * {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

export interface ICardRowProps extends React.HTMLAttributes<HTMLElement> {
  $columns: number;
  $gap?: number;
  /** Overrides the per-count card floor, for cards whose content needs more room. */
  $min?: number;
  /** False for open-ended lists, which fill columns and leave the last row short. */
  $balance?: boolean;
  as?: 'div' | 'section';
  'data-columns'?: number;
}

/**
 * The drawn N-up row, measured with the Intelligence `useMeasuredWidth` hook so it sheds columns
 * when the content area narrows (Orbit open, small viewport) instead of squeezing cards until their
 * contents overlap. A measured column COUNT on an explicit grid, not `repeat(auto-fit)`, which
 * never re-counts inside a flex item.
 */
const CardRow: React.FC<ICardRowProps> = ({
  $columns,
  $gap = ROW_GAP,
  $min,
  $balance = true,
  as,
  children,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const width = useMeasuredWidth(ref);
  const min = $min ?? MIN_CARD_WIDTH[$columns] ?? 220;
  const fit = fitColumns($columns, width, min, $gap);
  const cards = React.Children.toArray(children);
  const layout = $balance ? rowLayout(fit, cards.length) : { columns: fit, rows: [] };
  const spans = layout.rows.length > 1 ? cardSpans(layout) : [];
  const stretched = spans.some(span => span !== 1);

  return (
    <Grid
      ref={ref}
      as={as}
      $count={layout.columns}
      $gap={$gap}
      data-fit-columns={layout.columns}
      data-rows={layout.rows.join('+') || undefined}
      {...rest}
    >
      {stretched
        ? cards.map((card, index) => (
            // The span wrapper takes the card's own key, so React keeps each card's state.
            <SpanCell
              key={React.isValidElement(card) ? card.key ?? index : index}
              $span={spans[index]}
            >
              {card}
            </SpanCell>
          ))
        : children}
    </Grid>
  );
};

export default CardRow;
